/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import QueryFlowUtil from './QueryFlowUtil';
import Operator from './Operator';
import ShortcutUtils from './ShortcutUtils';
import InstanceType from './InstanceType';
import MoserObjectUtils from './MoserObjectUtils';
import ModuleUtils from './ModuleUtils';
import EditModeType from './EditModeType';
import IdGenerator from './IdGenerator';
import MoserObject from './MoserObject';
import EditModeUtils from './EditModeUtils';
import PropertySetter from './PropertySetter';
import MoserCommonUtils from './MoserCommonUtils';
import QsClassifierType from './QsClassifierType';
import QuerySubjectUtils from './QuerySubjectUtils';
import TreatAs from './TreatAs';
import TransientSourceType from './TransientSourceType';
import DataModuleExtractor from './DataModuleExtractor';
import ConvertQSType from './ConvertQSType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/client/queryFlow/QueryFlowUtil (runtime) // new
//@import com/ibm/bi/platform/moser/client/queryFlow/Operator (static) // Operator
//@import com/ibm/bi/platform/moser/common/utils/ShortcutUtils (static) // copyPropertiesFromReference, updateItemTypeParent, isShortcutValid, cleanShortcutItemPropertyOverride, isAliasValid
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getItems, getShortcutReferences, getFirstLevelQueryItems, getList
//@import com/ibm/bi/platform/moser/core/module/util/ModuleUtils (static) // deleteRelationshipsForQS, removeMembers, resolveShortcutQSRef, relink, convertReferenceQuerySubjectToCopy, canMarkAsTransientRel, getQuerySubjectById
//@import com/ibm/bi/platform/moser/common/generated/metadata/EditModeType (static) // EditModeType
//@import com/ibm/bi/platform/moser/core/module/util/IdGenerator (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (runtime) // getModuleObject
//@import com/ibm/bi/platform/moser/common/utils/EditModeUtils (static) // setEditMode
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (static) // getQueryItems, addShortcutQIToItemNormalization, updateIdForExpression
//@import com/ibm/bi/platform/moser/common/utils/MoserCommonUtils (runtime) // getObject
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (static) // QsClassifierType
//@import com/ibm/bi/platform/moser/core/module/util/QuerySubjectUtils (static) // addAllRef, clearEmbeddedFilter, getParentModule, clearRef, clearOriginalRef
//@import com/ibm/bi/platform/moser/common/generated/metadata/TreatAs (static) // TreatAs
//@import com/ibm/bi/platform/moser/common/generated/metadata/TransientSourceType (static) // TransientSourceType
//@import com/ibm/bi/platform/moser/core/module/util/DataModuleExtractor (static) // emptyList
//@import com/ibm/bi/platform/moser/client/conversion/ConvertQSType (static) // ConvertQSType

/**
 * This class provides a set of static utility APIs for converting objects. This class will be translated to JavaScript
 * @author  alan-tran
 */
export default class ConversionUtils extends BaseMoserObject{

	/**
	 * Converts a query subject to the given convert type. Currently only supported for a single query subject.
	 * @param {Array} moserObjects  list of moser objects to convert.
	 * @param {ConvertQSType} convertType   type to convert to.
	 */
	static convertQuerySubject(moserObjects, convertType) {
		if (!moserObjects) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = moserObjects, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var moserObj = __exp_enFor0[__i_enFor0];
			var converted = false;
			if (moserObj.getObjectType() == "QuerySubject") {
				var qs = moserObj;
				var queryFlow = new QueryFlowUtil();
				if (queryFlow.getQuerySubjectType(qs) == Operator.VIEW) {
					ConversionUtils._convertView(qs, convertType);
					converted = true;
				} else if (ShortcutUtils.isShortcutValid(qs)) {
					ConversionUtils._convertShortcut(qs, convertType);
					converted = true;
				} else if (ShortcutUtils.isAliasValid(qs)) {
					ConversionUtils._convertAlias(qs, convertType);
					converted = true;
				} else if (qs.getInstanceType() == InstanceType.REFERENCE) {
					ConversionUtils._convertLinked(qs, convertType);
					converted = true;
				}
				if (converted) {
					for (var __i_enFor1 = 0, __exp_enFor1 = MoserObjectUtils.getShortcutReferences(qs), __len_enFor1 = __exp_enFor1.length;
							__i_enFor1 < __len_enFor1; ++__i_enFor1) {
						var reference = __exp_enFor1[__i_enFor1];
						ModuleUtils.relink(reference, qs);
					}
				}
			}
		}
	}

	/**
	 * Converts a view to a given convert type. Supported conversion types include to shortcut and alias.
	 * @param {QuerySubject} view  	       view query subject
	 * @param {ConvertQSType} convertType   type to convert to.
	 */
	static _convertView(view, convertType) {
		switch (convertType) {
		case ConvertQSType.SHORTCUT:
			ConversionUtils._convertViewToShortcutReference(view);
			return;
		case ConvertQSType.ALIAS:
			ConversionUtils._convertViewToAlias(view);
			return;
		default:
			return;
		}
	}

	/**
	 * Converts a view to a shortcut with no treat as. Removes all inherited columns from the view. Re-adds all columns from the reference to the view.
	 * @param {QuerySubject} view  view query subject
	 */
	static _convertViewToGenericShortcut(view, refQSList) {
		var idGen;
		view.setInstanceType(InstanceType.REFERENCE);
		view.setEditMode(EditModeType.RWPARTIAL);
		var reference = refQSList[0];
		var embeddedFilters = view.basicGetFilter();
		if (embeddedFilters && !(embeddedFilters.length==0)) {
			idGen = new IdGenerator(reference);
		}
		var viewModule = MoserObject.getModuleObject(view);
		var refModule = MoserObject.getModuleObject(reference);
		var viewItems = [];
		var targetItemIds = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getItems(reference), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			targetItemIds[obj.getIdentifier()] = 1;
		}
		try {
			for (var i = view.getItem().length - 1; i >= 0; --i) {
				var item = view.getItem()[i];
				viewItems.push(item.clone());
				view.removeItemAt(i);
			}
			var refQSClone = reference.clone();
			ModuleUtils.removeMembers(refQSClone);
			var ignoreList = [];
			ignoreList.push(91);
			ignoreList.push(129);
			ignoreList.push(161);
			ignoreList.push(28);
			ignoreList.push(9);
			ignoreList.push(157);
			ShortcutUtils.copyPropertiesFromReference(view, refQSClone, ignoreList);
			EditModeUtils.setEditMode(refQSClone, EditModeType.RO);
			ShortcutUtils.cleanShortcutItemPropertyOverride(refQSClone);
			var targetQIIds = {};
			for (var __i_enFor1 = 0, __exp_enFor1 = PropertySetter.getQueryItems(refQSClone), __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var qi = __exp_enFor1[__i_enFor1];
				targetQIIds[qi.getIdentifier()] = 1;
			}
			for (var __i_enFor2 = 0, __exp_enFor2 = refQSClone.getItem(), __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var item = __exp_enFor2[__i_enFor2];
				view.addItem(item);
			}
			var extQueryItems = ConversionUtils._processViewItems(view, viewItems, targetItemIds);
			PropertySetter.addShortcutQIToItemNormalization(view, targetQIIds, extQueryItems, viewModule.getObjectFactory(), true);
			ShortcutUtils.updateItemTypeParent(refQSClone.getItem(), view);
			ModuleUtils.resolveShortcutQSRef(view, viewModule, refQSClone, refModule);
		} catch (e) {
			throw e;
		}

		PropertySetter.updateIdForExpression(view.basicGetItem(), view.getIdentifier());
		if (idGen) {
			for (var __i_enFor3 = 0, __exp_enFor3 = embeddedFilters, __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var filter = __exp_enFor3[__i_enFor3];
				if (!(idGen.isIdUniqueInScope(filter.getIdentifier()))) {
					idGen.generateId(filter);
					filter.setIdForExpression(view.getIdentifier() + "." + filter.getIdentifier());
				}
			}
		}
	}

	/**
	 * Adds a list of items back to the view. Removes any items that conflict with the items from the view's target. Returns a list of non-conflicting extended query items.
	 * @param {QuerySubject} view           view query subject
	 * @param {Array} items          list of items from the view
	 * @param {Set} targetItemIds  set of item identifiers from the view's target.
	 */
	static _processViewItems(view, items, targetItemIds) {
		var extQueryItems = [];
		for (var i = items.length - 1; i >= 0; --i) {
			var item = items[i];
			ConversionUtils._processViewItem(view, item, targetItemIds, null, false, extQueryItems);
		}
		return extQueryItems;
	}

	/**
	 * Adds the obj to the end of the view if it does not conflict with any target items. Adds to list of extended items if the obj is a query item. Performs this action for all children of the obj.
	 * @param {QuerySubject} view                 view query subject
	 * @param {ItemType} obj                  ItemType
	 * @param {Set} targetItemIds        set of item identifiers from the view's target
	 * @param {ObjectType} parentObjType        parentObjType
	 * @param {boolean} parentObjIdConflict  parent of obj identifier conflicts with an item from view's target
	 * @param {Array} extQueryItems        list of extended items
	 */
	static _processViewItem(view, obj, targetItemIds, parentObjType, parentObjIdConflict, extQueryItems) {
		var objectType = MoserCommonUtils.getObject(obj);
		var objConflicts = false;
		if (!objectType) {
			return;
		}
		var objType = objectType.getObjectType();
		var childItems = ConversionUtils._getChildItems(objectType);
		if (targetItemIds.hasOwnProperty(objectType.getIdentifier())) {
			objConflicts = true;
			ConversionUtils._removeItemTypeFromParent(obj, parentObjType);
		} else if (parentObjIdConflict || !obj.getParent()) {
			view.addItem(obj);
		}
		if (!objConflicts && "QueryItem" == objType) {
			extQueryItems.push(objectType);
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = childItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var childItem = __exp_enFor0[__i_enFor0];
			ConversionUtils._processViewItem(view, childItem, targetItemIds, objectType, objConflicts, extQueryItems);
		}
	}

	/**
	 * Removes obj from parentObjType
	 * @param {ItemType} obj                  ItemType
	 * @param {ObjectType} parentObjType        parent ObjectType of obj
	 */
	static _removeItemTypeFromParent(obj, parentObjType) {
		if (!obj || !parentObjType) {
			return;
		}
		var parentType = parentObjType.getObjectType();
		if ("QueryItem" == parentType) {
			var qi = parentObjType;
			qi.removeItem(obj);
		} else if ("Folder" == parentType) {
			var folder = parentObjType;
			folder.removeItem(obj);
		} else if ("Calculation" == parentType) {
			var calc = parentObjType;
			calc.removeItem(obj);
		}
	}

	/**
	 * Returns the child list of ItemType objects from an ObjectType
	 * @param {ObjectType} objectType           object type
	 */
	static _getChildItems(objectType) {
		var items;
		var itemsCopy = [];
		var objType = objectType.getObjectType();
		if ("QueryItem" == objType) {
			var qi = objectType;
			items = qi.basicGetItem();
		} else if ("Folder" == objType) {
			var folder = objectType;
			items = folder.basicGetItem();
		} else if ("Calculation" == objType) {
			var calc = objectType;
			items = calc.basicGetItem();
		}
		if (!items) {
			return itemsCopy;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			itemsCopy.push(item);
		}
		return itemsCopy;
	}

	static _removeRelFromModule(module, qs, instanceTypes) {
		if (!module || !qs) {
			return;
		}
		var rels = MoserObjectUtils.getList(module.basicGetRelationship());
		ModuleUtils.deleteRelationshipsForQS(module, rels, qs.getIdentifier(), instanceTypes);
	}

	/**
	 * Converts a shortcut or alias into a view.
	 * @param {QuerySubject} qs      shortcut or alias query subject.
	 * @param {Module} module
	 */
	static _convertGenericShortcutToView(qs, module) {
		var instanceTypes = [];
		instanceTypes.push(InstanceType.REFERENCE);
		instanceTypes.push(InstanceType.MISSING);
		ConversionUtils._removeRelFromModule(module, qs, instanceTypes);
		qs.setInstanceType(InstanceType.COPY);
		qs.setTreatAs(null);
		qs.setClassifier(QsClassifierType.QUERY_SUBJECT);
		var originalRef = qs.getOriginalRef();
		if (!(originalRef.length==0)) {
			QuerySubjectUtils.clearRef(qs);
			QuerySubjectUtils.addAllRef(qs, originalRef);
			QuerySubjectUtils.clearOriginalRef(qs);
			ConversionUtils._updateItemExpressionsToView(qs, qs.getRef()[0]);
			var items = qs.basicGetItem();
			if (items) {
				try {
					var itemsCloned = [];
					for (var i = items.length - 1; i >= 0; --i) {
						var item = items[i];
						EditModeUtils.setEditMode(item, EditModeType.RO);
						var itemClone = item.clone();
						itemsCloned.push(itemClone);
						qs.removeItemAt(i);
					}
					for (var i = itemsCloned.length - 1; i >= 0; --i) {
						var itemClone = itemsCloned[i];
						qs.addItem(itemClone);
					}
				} catch (e) {
					throw e;
				}

			}
			EditModeUtils.setEditMode(qs, null);
		}
	}

	/**
	 * Converts a view to a shortcut.
	 * @param {QuerySubject} view  view query subject
	 */
	static _convertViewToShortcutReference(view) {
		var queryFlow = new QueryFlowUtil();
		var refQSList = queryFlow.getPrecursors(view);
		if (refQSList.length == 1) {
			ConversionUtils._convertViewToGenericShortcut(view, refQSList);
			QuerySubjectUtils.clearEmbeddedFilter(view);
			view.setTreatAs(TreatAs.REFERENCE);
		}
	}

	/**
	 * Converts a view to an alias.
	 * @param {QuerySubject} view  view query subject
	 */
	static _convertViewToAlias(view) {
		var queryFlow = new QueryFlowUtil();
		var refQSList = queryFlow.getPrecursors(view);
		if (refQSList.length == 1) {
			ConversionUtils._convertViewToGenericShortcut(view, refQSList);
			view.setTreatAs(TreatAs.ALIAS);
		}
	}

	/**
	 * Converts a shortcut to a given convert type. Supported conversion types include to alias, view, and table.
	 * @param {QuerySubject} shortcut  	   shortcut query subject
	 * @param {ConvertQSType} convertType   type to convert to.
	 */
	static _convertShortcut(shortcut, convertType) {
		var module = MoserObject.getModuleObject(shortcut);
		switch (convertType) {
		case ConvertQSType.ALIAS:
			shortcut.setTreatAs(TreatAs.ALIAS);
			var instanceTypes = [];
			instanceTypes.push(InstanceType.REFERENCE);
			instanceTypes.push(InstanceType.MISSING);
			ConversionUtils._removeRelFromModule(module, shortcut, instanceTypes);
			return;
		case ConvertQSType.VIEW:
			ConversionUtils._convertGenericShortcutToView(shortcut, module);
			return;
		case ConvertQSType.TABLE:
			try {
				ModuleUtils.convertReferenceQuerySubjectToCopy(shortcut, module);
			} catch (e) {
				throw e;
			}

			return;
		default:
			return;
		}
	}

	/**
	 * Converts an alias to a given convert type. Supported conversion types include to shortcut and view.
	 * @param {QuerySubject} alias  	  alias query subject
	 * @param {ConvertQSType} convertType  type to convert to.
	 */
	static _convertAlias(alias, convertType) {
		var module = MoserObject.getModuleObject(alias);
		switch (convertType) {
		case ConvertQSType.SHORTCUT:
			alias.setTreatAs(TreatAs.REFERENCE);
			QuerySubjectUtils.clearEmbeddedFilter(alias);
			return;
		case ConvertQSType.VIEW:
			ConversionUtils._convertGenericShortcutToView(alias, module);
			return;
		case ConvertQSType.TABLE:
			try {
				ModuleUtils.convertReferenceQuerySubjectToCopy(alias, module);
			} catch (e) {
				throw e;
			}

			return;
		default:
			return;
		}
	}

	/**
	 * Converts a linked query subject to a given convert type. Supported conversion types include to shortcut, alias, and table.
	 * @param {QuerySubject} linkedQS  	  linked query subject
	 * @param {ConvertQSType} convertType  type to convert to.
	 */
	static _convertLinked(linkedQS, convertType) {
		switch (convertType) {
		case ConvertQSType.SHORTCUT:
			ConversionUtils._convertLinkToShortcut(linkedQS, TreatAs.REFERENCE);
			return;
		case ConvertQSType.ALIAS:
			ConversionUtils._convertLinkToShortcut(linkedQS, TreatAs.ALIAS);
			return;
		case ConvertQSType.TABLE:
			var module = MoserObject.getModuleObject(linkedQS);
			try {
				ModuleUtils.convertReferenceQuerySubjectToCopy(linkedQS, module);
			} catch (e) {
				throw e;
			}

			return;
		default:
			return;
		}
	}

	/**
	 * Converts a linked query subject to either a shortcut or alias.
	 * @param {QuerySubject} linkedQS  query subject reference
	 * @param  TreatAs   shortcut type
	 */
	static _convertLinkToShortcut(linkedQS, treatAs) {
		QuerySubjectUtils.clearEmbeddedFilter(linkedQS);
		linkedQS.setTreatAs(treatAs);
		linkedQS.setEditMode(EditModeType.RWPARTIAL);
		var module = QuerySubjectUtils.getParentModule(linkedQS);
		var qsId = linkedQS.getIdentifier();
		var rels = MoserObjectUtils.getList(module.basicGetRelationship());
		var instanceTypes = [];
		if (TreatAs.REFERENCE == treatAs) {
			ConversionUtils._convertRelToTransientRel(linkedQS, rels, module);
			instanceTypes.push(InstanceType.MISSING);
			ModuleUtils.deleteRelationshipsForQS(module, rels, qsId, instanceTypes);
		}
		if (TreatAs.ALIAS == treatAs) {
			instanceTypes.push(InstanceType.REFERENCE);
			instanceTypes.push(InstanceType.MISSING);
			ModuleUtils.deleteRelationshipsForQS(module, rels, qsId, instanceTypes);
		}
	}

	/**
	 * Converts linked/reference relationships to a transient/dotted relationship
	 * @param {QuerySubject} convertedLinkedQS  linked query subject converted to shortcut
	 * @param {Array} rels               list of relationships
	 * @param {Module} module             data module
	 */
	static _convertRelToTransientRel(convertedLinkedQS, rels, module) {
		var qsId = convertedLinkedQS.getIdentifier();
		for (var __i_enFor0 = 0, __exp_enFor0 = rels, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var rel = __exp_enFor0[__i_enFor0];
			if (InstanceType.REFERENCE != rel.getInstanceType()) {
				continue;
			}
			var qsOther = null;
			if (qsId == rel.getLeft().getRef()) {
				qsOther = ModuleUtils.getQuerySubjectById(rel.getRight().getRef(), module);
			}
			if (qsId == rel.getRight().getRef()) {
				qsOther = ModuleUtils.getQuerySubjectById(rel.getLeft().getRef(), module);
			}
			if (qsOther && ModuleUtils.canMarkAsTransientRel(convertedLinkedQS, qsOther)) {
				rel.setTransientSource(TransientSourceType.MODULE);
			}
		}
	}

	/**
	 * Updates query item and filter item expressions for query subjects converting to view.
	 * @param {QuerySubject} qs   query subject turning to view
	 * @param {String} ref  query subject ref
	 */
	static _updateItemExpressionsToView(qs, ref) {
		var queryItems = MoserObjectUtils.getFirstLevelQueryItems(qs);
		for (var __i_enFor0 = 0, __exp_enFor0 = queryItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			if (DataModuleExtractor.emptyList(qi.basicGetPropertyOverride())) {
				qi.setExpression(ref + "." + qi.getIdentifier());
			}
		}
	}

	/**
	 * Returns true if the type passed is a shortcut. Not used. This method is in place to allow the proper translation of this file. To correctly import ConvertQSType into generated js file.
	 * @param {ConvertQSType} type   convert query subject type
	 */
	static _isConvertTypeShortcut(type) {
		return type == ConvertQSType.SHORTCUT;
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ConversionUtils.js